import { df } from '@/plugins/firebase'

/**
 * Factory function for product cache functionality
 * Reduces redundant Firebase reads with a 5-minute TTL cache
 */
export function useProductCache () {
  // OPTIMIZATION: Product cache Map with 5-minute TTL
  const productCache = new Map()
  const cacheExpiry = 5 * 60 * 1000 // 5 minutes

  /**
   * Get product from cache or fetch from Firebase
   * @param {string} pricelistId - The pricelist ID
   * @param {string} itemId - The item ID
   * @returns {Promise<Object>} The product data
   */
  async function getProductFromCache (pricelistId, itemId) {
    const cacheKey = `${pricelistId}-${itemId}`

    // Check cache first
    const cached = productCache.get(cacheKey)
    if (cached && Date.now() - cached.timestamp < cacheExpiry) {
      return cached.data
    }

    // Fetch from Firebase
    const product = await df.doc(`location_pricelist/${pricelistId}/items/${itemId}`).get()
    const data = product.data()

    // Cache result
    productCache.set(cacheKey, {
      data: data,
      timestamp: Date.now()
    })

    return data
  }

  /**
   * Get product by barcode from cache or fetch from Firebase
   * @param {string} pricelistId - The pricelist ID
   * @param {string} barcode - The barcode to search for
   * @param {string} currency - The currency code
   * @returns {Promise<Object|null>} The product data or null if not found
   */
  async function getProductByBarcode (pricelistId, barcode, currency) {
    const cacheKey = `${pricelistId}-barcode-${barcode}`

    // Check cache first
    const cached = productCache.get(cacheKey)
    if (cached && Date.now() - cached.timestamp < cacheExpiry) {
      console.log(`Cache hit for barcode: ${barcode}`)
      return cached.data
    }

    console.log(`Cache miss for barcode: ${barcode}, fetching from Firestore...`)

    // Fetch from Firestore
    const snapshot = await df.collection(`location_pricelist/${pricelistId}/items`)
      .where('status', '==', 'OK')
      .where('active', '==', true)
      .where(`prices.${currency}.active`, '==', true)
      .where('barcodes', 'array-contains', barcode)
      .limit(1)
      .get()

    if (snapshot.empty || !snapshot.docs || snapshot.docs.length === 0) {
      console.log(`No product found for barcode: ${barcode}`)
      return null
    }

    const data = snapshot.docs[0].data()

    // Cache by barcode
    productCache.set(cacheKey, {
      data: data,
      timestamp: Date.now()
    })

    // Also cache by item ID for consistency
    const itemCacheKey = `${pricelistId}-${data.id}`
    productCache.set(itemCacheKey, {
      data: data,
      timestamp: Date.now()
    })

    console.log(`Cached product for barcode: ${barcode} and itemId: ${data.id}`)

    return data
  }

  /**
   * Clear all cached products
   */
  function clearProductCache () {
    if (productCache) {
      productCache.clear()
    }
  }

  return {
    productCache,
    getProductFromCache,
    getProductByBarcode,
    clearProductCache
  }
}
